<template>
  <div>
    <page-header class="initiative-header" title="CTO Circle Privacy Policy" />
    <div class="container my-5">
      <p>
        This Privacy Policy describes the Company's practices regarding the
        collection, use and disclosure of the information we collect from and
        about you when you use any application (the "Service", "Application")
        operated by us ("Company", "we", "us") in partnership with a church or
        similar social organization ("Organization").
      </p>
      <p>
        We take our obligations regarding your privacy seriously and have made
        every effort to draft this Privacy Policy in a manner that is clear and
        easy for you to understand. By accessing or using the Service, you agree
        to this Privacy Policy, and our Terms of Service.
      </p>
      <h2>1. Information We Collect</h2>
      <h3>1.1 Information You Provide to Us</h3>
      <p>
        If you create an account, you will be invited to provide us with certain
        information ("Personal Data"), including your name and profile picture
        ("Profile Information"), your email address, and phone number ("Contact
        Information"), and any other personal information you choose to provide
        ("Further Information").
      </p>

      <h3>1.2 Content You Create</h3>
      <p>
        In the course of using the Service, you may post "Content", including
        but not limited to comments, photos, prayer requests, articles, and
        events.
      </p>

      <h3>1.3 Information We Collect Automatically</h3>
      <p>
        We use technologies like cookies and pixel tags to provide, monitor,
        analyze, promote and improve the Service. For example, a cookie is used
        to remember your user name when you return to the Service and to improve
        our understanding of how you interact with the Service. You can block
        cookies on your web browser; however please be aware that some features
        of the Service may not function properly if the ability to accept
        cookies is disabled.
      </p>

      <p class="subtitle">1.3.1 Log Files</p>
      <p>
        When you use the Service, our servers automatically record certain
        information in server logs. These server logs may include information
        such as your web request, Internet Protocol ("IP") address, browser
        type, referring / exit pages and URLs, number of clicks and how you
        interact with links on the Service, domain names, landing pages, pages
        viewed, mobile carrier, and other such information. Log files help us to
        monitor, analyze, improve and maintain the Service and to diagnose and
        fix any Service-related issues.
      </p>

      <p class="subtitle">1.3.2 Device Identifiers</p>
      <p>
        When you access the Service using a mobile device, we collect specific
        device information contained in your mobile device's "device
        identifier." This device identifier includes information such as the
        type of device you are using, its operating system, and mobile network
        information, which may include your mobile phone number. We may
        associate this device identifier with your Service account and will use
        data associated with your device identifier to customize our Service to
        your device and to analyze any device-related issues.
      </p>

      <p class="subtitle">1.3.3 Location Information</p>
      <p>
        We may collect and process information about the location of the device
        from which you are accessing the Service. Location data may convey
        information about how you browse the Service and may be used in
        conjunction with personally identifiable information. You can disable
        location based services in settings associated with the Service; however
        please be aware that some features of the Service may not function
        properly if location services are turned off.
      </p>
      <h2>2. How We Share Your Information</h2>
      <p>
        We may share the information we collect from you with third parties as
        detailed below.
      </p>

      <h3>2.1 With Other Users</h3>
      <p>
        The Service is explicitly intended to build community in the context of
        the Organization, and consequently your Personal Data may, through your
        use of the Service, be made available to other Users of the Application.
        This includes, without limitation:
      </p>

      <ol type="a">
        <li>
          Your Profile Information will be visible to all Users of the
          Application
        </li>
        <li>
          Your Contact Information will be visible to Users authorised to manage
          groups, events, and volunteer opportunities that you elect to join
          ("Managers")
        </li>
        <li>
          All of your Personal Information will be visible to Users authorised
          by the Organization to&nbsp;manage the Application ("Administrators")
        </li>
      </ol>

      <p>
        You explicitly acknowledge that both the Company and the Organization
        cannot control, and therefore cannot be held liable for any data breach
        resulting from the actions of Users.
      </p>

      <h3>2.2 With Trusted Service Providers and Business Partners.</h3>
      <p>
        We may utilize trusted third party service providers to assist us in
        delivering our Service. For example, we may use third parties to help
        host our Service, send out email updates, or process payments. These
        service providers may have access to your information for the limited
        purpose of providing the service we have contracted with them to
        provide. They are required to have a privacy policy and security
        standards in place that are at least as protective of your information
        as is this Privacy Policy. We may also store personal information in
        locations outside the direct control of the Company (for instance, on
        servers or databases co-located with hosting providers).
      </p>

      <h3>2.3 With Law Enforcement or In Order to Protect Our Rights.</h3>
      <p>
        We may disclose your information (including your personally identifiable
        information) if required to do so by law or subpoena and if the relevant
        request meets our Law Enforcement Guidelines. We may also disclose your
        information to our legal counsel, governmental authorities or law
        enforcement if we believe that it is reasonably necessary to do so in
        order to comply with a law or regulation; to protect the safety of any
        person; to address fraud, security or technical issues; or to protect
        the Company or the Organization's rights or property.
      </p>

      <h3>2.4 In an Aggregate and Non-Personally Identifiable Manner.</h3>
      <p>
        We may disclose aggregate non-personally identifiable information (such
        as aggregate and anonymous usage data, platform types, etc.) about the
        overall use of our Service publicly or with interested third parties to
        help them understand, or to help us improve, the Service.
      </p>

      <h3>2.5 In Connection With a Sale or Change of Control.</h3>
      <p>
        If the ownership of all or substantially all of our business changes, we
        may transfer your information to the new owner so that the Service can
        continue to operate. In such case, your information would remain subject
        to the promises and commitments contained in this Privacy Policy until
        such time as this Privacy Policy is updated or amended by the acquiring
        party upon notice to you.
      </p>

      <h1><strong>3. How We Protect Your Information</strong></h1>
      <p>
        The security of your information is important to us. When you enter
        sensitive information (such as a credit card number) as part of our
        service, we encrypt the transmission of that information using
        industry-standard encryption.
      </p>

      <p>
        The Company uses commercially reasonable and industry-standard physical,
        managerial, and technical safeguards to preserve the integrity and
        security of your information. For example, we continuously and regularly
        back up your data to help prevent data loss and aid in data recovery. We
        also guard against common web attack vectors, and implement firewalls
        and access restrictions on our servers to secure our network and better
        protect your information.
      </p>

      <p>
        Although we allow you control over where you share your Content and what
        information is included in your Profile and take reasonable steps to
        maintain the security if the information associated with your account,
        please be aware that no security measures are perfect or impenetrable.
        Neither the Company nor the Organization cannot control the actions of
        other Users, and neither party is responsible for third party
        circumvention of any privacy settings or security measures on the
        Service.
      </p>

      <h1><strong>4. Your Choices About Your Information</strong></h1>
      <p>
        You may, of course, decline to submit personally identifiable
        information through the Service, in which case the Company may not be
        able to provide certain services to you. You may amend your Personal
        Information at any time, either through mechanisms provided in the
        Application, or by emailing privacy@ctocircle.com.
      </p>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/global/PageHeader';
export default {
  components: {
    PageHeader,
  },
};
</script>

<style scoped>
</style>